import React, { useState, useEffect, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import { useWarehouse } from '../../context/WarehouseContext';
import { useClient } from '../../context/ClientContext';
import { useAuth } from '../../context/AuthContext';

// Roles with dedicated scoping behavior baked into this form. Any other role
// (including custom roles created in Roles & Permissions) falls back to
// showing both scoping fields as optional, since we don't know its intent.
const KNOWN_ROLES = ['admin', 'store_executive', 'dispatch_executive', 'client_user'];
const roleChipClass = {
  admin:              'chip-red',
  store_executive:    'chip-blue',
  dispatch_executive: 'chip-purple',
  client_user:        'chip-amber',
};

const emptyForm = { name: '', email: '', password: '', role: '', warehouse: '', client: '' };

const UserManagement = () => {
  const { warehouses } = useWarehouse();
  const { clients } = useClient();
  const { can } = useAuth();
  const canManage = can('users.manage');
  const [users, setUsers]         = useState([]);
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const roleLabel = useMemo(() => {
    const map = { super_admin: 'Super Admin' };
    roles.forEach((r) => { map[r.key] = r.name; });
    return map;
  }, [roles]);

  const isClientUser   = form.role === 'client_user';
  const needsWarehouse  = form.role === 'store_executive' || form.role === 'dispatch_executive';
  const isCustomRole    = form.role !== '' && !KNOWN_ROLES.includes(form.role);
  const showWarehouseField = needsWarehouse || isCustomRole;
  const showClientField    = isClientUser || isCustomRole;

  const fetchUsers = () => {
    api.get('/users')
      .then((r) => setUsers(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (!canManage) return;
    api.get('/users/roles')
      .then((r) => setRoles(r.data.data))
      .catch(console.error);
  }, [canManage]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (needsWarehouse && !form.warehouse) {
      return toast.error('Please assign a warehouse to this user');
    }
    if (isClientUser && !form.client) {
      return toast.error('Please assign a client to this portal user');
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!needsWarehouse) delete payload.warehouse;
      if (!isClientUser) delete payload.client;
      await api.post('/auth/register', payload);
      toast.success('User created successfully');
      setShowForm(false);
      setForm(emptyForm);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user._id}`, { isActive: !user.isActive });
      toast.success(`User ${user.isActive ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const columns = [
    {
      label: 'Name', key: 'name',
      render: (r) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-zip-red/20 border border-zip-red/30 flex items-center justify-center flex-shrink-0">
            <span className="text-zip-red text-xs font-bold font-mono">{r.name?.[0]?.toUpperCase()}</span>
          </div>
          <span className="font-medium text-zip-text">{r.name}</span>
        </div>
      ),
    },
    {
      label: 'Email', key: 'email',
      render: (r) => <span className="font-mono text-zip-muted text-xs">{r.email}</span>,
    },
    {
      label: 'Role', key: 'role',
      render: (r) => <span className={roleChipClass[r.role] ?? 'chip-gray'}>{roleLabel[r.role] ?? r.role}</span>,
    },
    {
      label: 'Scope', key: 'scope',
      render: (r) => r.role === 'client_user'
        ? (r.client
            ? <span className="font-mono text-zip-muted text-xs">Client · {r.client.code}</span>
            : <span className="text-zip-muted/50 text-xs">—</span>)
        : r.warehouse
          ? <span className="font-mono text-zip-muted text-xs">{r.warehouse.code} · {r.warehouse.city}</span>
          : <span className="text-zip-muted/50 text-xs">All</span>,
    },
    {
      label: 'Status', key: 'isActive',
      render: (r) => <span className={r.isActive ? 'chip-green' : 'chip-gray'}>{r.isActive ? 'Active' : 'Inactive'}</span>,
    },
    {
      label: 'Created', key: 'createdAt',
      render: (r) => (
        <span className="font-mono text-zip-muted text-xs">
          {format(new Date(r.createdAt), 'dd MMM yyyy')}
        </span>
      ),
    },
    {
      label: '', key: 'actions',
      render: (r) => canManage ? (
        <button
          onClick={() => toggleActive(r)}
          className={`text-xs font-mono transition-colors
            ${r.isActive ? 'text-zip-muted hover:text-zip-red' : 'text-emerald-600 hover:text-emerald-700'}`}
        >
          {r.isActive ? 'Deactivate' : 'Activate'}
        </button>
      ) : null,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Action bar */}
      {canManage && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={15} /> Add User
          </button>
        </div>
      )}

      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-12 skeleton rounded" />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={users}
          emptyText="No users found."
        />
      )}

      {/* Create user modal */}
      <Modal
        open={showForm}
        onClose={() => { setShowForm(false); setForm(emptyForm); }}
        title="Add New User"
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {[
            { label: 'Full Name', key: 'name', type: 'text', placeholder: 'Jane Doe' },
            { label: 'Email Address', key: 'email', type: 'email', placeholder: 'jane@ziplogyx.com' },
            { label: 'Password', key: 'password', type: 'password', placeholder: '••••••••', min: 6 },
          ].map(({ label, key, type, placeholder, min }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">{label} *</label>
              <input
                type={type}
                className="zip-input"
                required
                minLength={min}
                placeholder={placeholder}
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              />
            </div>
          ))}

          <div>
            <label className="block text-xs font-medium text-zip-muted mb-1.5">Role *</label>
            <select
              className="zip-input"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value, warehouse: '', client: '' })}
              required
            >
              <option value="" disabled>— Select role —</option>
              {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          </div>

          {showWarehouseField && (
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">
                Assigned Warehouse {needsWarehouse && '*'}
              </label>
              <select
                className="zip-input"
                value={form.warehouse}
                onChange={(e) => setForm({ ...form, warehouse: e.target.value })}
                required={needsWarehouse}
              >
                <option value="">— Select warehouse —</option>
                {warehouses.map((wh) => (
                  <option key={wh._id} value={wh._id}>{wh.name} ({wh.code} · {wh.city})</option>
                ))}
              </select>
            </div>
          )}

          {showClientField && (
            <div>
              <label className="block text-xs font-medium text-zip-muted mb-1.5">
                Assigned Client {isClientUser && '*'}
              </label>
              <select
                className="zip-input"
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                required={isClientUser}
              >
                <option value="">— Select client —</option>
                {clients.map((c) => (
                  <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
                ))}
              </select>
              {isClientUser && (
                <p className="text-[11px] text-zip-muted/70 mt-1">This user will only see their own client's goods.</p>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="submit" disabled={submitting} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {submitting ? (
                <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating…</>
              ) : 'Create User'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setForm(emptyForm); }} className="btn-ghost">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagement;
